import * as React from 'react'
import {css} from '@emotion/react'
import styled from '@emotion/styled'

import {Brand} from '@vio/core/src/brand/types'
import {
  getBrandLogo,
  getBrandLogoIcon
} from '@vio/core/src/offer/business/provider'

interface Props extends React.ImgHTMLAttributes<HTMLImageElement> {
  /** Brand configuration. It can be one of our brands or a white-label brand. */
  brand: Brand
  /** Pass through classname to allow styles overrides */
  className?: string
  /** Whether to display the compact version */
  compact?: boolean
  /** Whether to display the inverse variant of the logo. Only used if `compact` is `false` */
  isInverse?: boolean
  /** Whether to display the icon with text. Only used if `compact` is `true` and the brand is `vio`. */
  withIconText?: boolean
}

const LogoImageElement = styled.img`
  margin: 0 auto;
  display: block;
  max-height: 100%;
  max-width: 100%;
`

const BrandIcon = ({
  withIconText,
  brand,
  className,
  alt = '',
  ...imageProps
}: Omit<Props, 'withWhiteText' | 'compact'>) => (
  <LogoImageElement
    alt={alt}
    title={brand.name}
    src={getBrandLogoIcon({
      providerCode: brand.code,
      withText: withIconText
    })}
    className={className}
    data-id="brand-logo"
    {...imageProps}
  />
)

const BrandFullLogo = ({
  brand,
  isInverse,
  className,
  alt = '',
  ...imageProps
}: Omit<Props, 'withIconText' | 'compact'>) => (
  <LogoImageElement
    alt={alt}
    title={brand.name}
    src={getBrandLogo({
      providerCode: brand.code,
      isInverse
    })}
    className={className}
    data-id="brand-logo"
    {...imageProps}
  />
)

export const BrandLogo = ({
  brand,
  compact = false,
  className,
  isInverse = false,
  withIconText = false,
  height = 30,
  ...imageProps
}: Props) => {
  // For all other brands, use the standard logo logic
  return compact ? (
    <BrandIcon
      brand={brand}
      withIconText={withIconText}
      className={className}
      height={height}
      {...imageProps}
    />
  ) : (
    <BrandFullLogo
      brand={brand}
      isInverse={isInverse}
      className={className}
      height={height}
      {...imageProps}
    />
  )
}

const LOGO_MAX_HEIGHT = 44

/**
 * Wrapper styles for a `BrandLogo` shown inside an overlay/auth header (SUSI
 * partner sign-up, splash message). Relocated from the removed Atlas
 * `CompactHeader`, trimmed to the rules these call sites actually use.
 */
export const logoStyles = css`
  display: flex;
  justify-content: center;
  align-items: center;
  max-height: ${LOGO_MAX_HEIGHT}px;
  width: auto;
  margin: 0;
`
